// src/components/Users/UserManagementView.jsx
import { useEffect, useRef, useState } from 'react';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import {
  MODULES as modules,
  PERMISSION_TYPES as permissionsTypes,
  makeFullPermissions,
  makeEmptyPermissions,
  normalizePermissionsMatrix,
  hasAccess,
} from '../../lib/modules';

// === Protección de cuenta crítica ===
const PROTECTED_EMAIL = 'programacionsebastian94@gmail.com';
const isProtected = (userOrEmail) => {
  const email = typeof userOrEmail === 'string' ? userOrEmail : (userOrEmail?.email || '');
  return email.trim().toLowerCase() === PROTECTED_EMAIL;
};




// ---- Helper: formatea Timestamps/fechas para la UI ----
const formatTS = (v) => {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && typeof v.toDate === 'function') {
    try { return v.toDate().toLocaleString(); } catch { /* ignore */ }
  }
  if (typeof v === 'object' && typeof v.seconds === 'number') {
    try { return new Date(v.seconds * 1000).toLocaleString(); } catch { /* ignore */ }
  }
  if (v instanceof Date) return v.toLocaleString();
  return String(v);
};

// ---- Roles disponibles ----
const roles = ['TI', 'Administrador', 'Supervisor', 'Cajero', 'Mesero'];

const fullNoNotice = () => {
  const p = makeFullPermissions();
  p['Avisos Globales'] = { Lectura: false, Edición: false, Eliminación: false };
  return p;
};

// ---- Plantillas por rol (solo lo que difiere de "todo permitido") ----
const defaultPermissions = {
  TI: fullNoNotice(),
  Administrador: fullNoNotice(),
  Supervisor: {
    'Punto de Venta': { Lectura: true, Edición: true, Eliminación: false },
    'Productos': { Lectura: true, Edición: true, Eliminación: false },
    'Inventarios': { Lectura: true, Edición: true, Eliminación: false },
    'Entradas': { Lectura: true, Edición: true, Eliminación: false },
    'Salidas': { Lectura: true, Edición: true, Eliminación: false },
    'Reportes': { Lectura: true, Edición: true, Eliminación: false },
    'Gastos': { Lectura: true, Edición: true, Eliminación: false },
    'Detalle de Venta': { Lectura: true, Edición: true, Eliminación: false },
    'Nómina': { Lectura: true, Edición: false, Eliminación: false },
    'Usuarios': { Lectura: false, Edición: false, Eliminación: false },
    'Clientes': { Lectura: true, Edición: true, Eliminación: false },
    'Auditoría': { Lectura: true, Edición: false, Eliminación: false },
    'Panel Financiero': { Lectura: true, Edición: false, Eliminación: false },
    'Configuración': { Lectura: false, Edición: false, Eliminación: false },
    'Proveedores': { Lectura: true, Edición: true, Eliminación: false },
    'Cierre de Turno': { Lectura: true, Edición: true, Eliminación: false },
    'Turnos de Caja (Admin)': { Lectura: false, Edición: false, Eliminación: false },
    'Toma de Pedidos': { Lectura: true, Edición: true, Eliminación: false },
    'Panel de Despacho': { Lectura: true, Edición: false, Eliminación: false },
  },
  Cajero: {
    'Punto de Venta': { Lectura: true, Edición: true, Eliminación: false },
    'Productos': { Lectura: true, Edición: false, Eliminación: false },
    'Inventarios': { Lectura: true, Edición: false, Eliminación: false },
    'Entradas': { Lectura: true, Edición: false, Eliminación: false },
    'Salidas': { Lectura: true, Edición: false, Eliminación: false },
    'Detalle de Venta': { Lectura: true, Edición: false, Eliminación: false },
    'Clientes': { Lectura: true, Edición: false, Eliminación: false },
    'Cierre de Turno': { Lectura: true, Edición: true, Eliminación: false },
    // el resto queda en false por defecto
  },
  Mesero: {
    'Toma de Pedidos': { Lectura: true, Edición: true, Eliminación: false },
    'Panel de Despacho': { Lectura: true, Edición: false, Eliminación: false },
    'Clientes': { Lectura: true, Edición: false, Eliminación: false },
    // el resto en false por defecto
  },
};

// Normaliza: asegura que existan TODOS los módulos (los no definidos -> false)
const normalizePermissions = (perms = {}, role) => {
  const base = defaultPermissions[role] || {};
  return { ...makeEmptyPermissions(), ...base, ...normalizePermissionsMatrix(perms) };
};

const parseApiJson = async (res) => {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (_e) {
    return { error: text || 'Respuesta inválida del servidor' };
  }
};

const UserManagementView = ({ onBack, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // {id, uid, name, email, role, permissions, isActive}
  const [deletingUid, setDeletingUid] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [confirmingUser, setConfirmingUser] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const savingUserRef = useRef(false);
  const editingUid = editingUser?.uid || editingUser?.id || '';
  const isEditingSelf = !!editingUid && String(editingUid) === String(currentUser?.uid || currentUser?.id || '');
  const restrictSensitiveEditing = isEditingSelf || isProtected(editingUser) || editingUser?.role === 'TI';

// Solo esta cuenta puede gestionarse a sí misma
const canManageProtected = (currentUser?.email || '').trim().toLowerCase() === PROTECTED_EMAIL;
const canReadUsers = canManageProtected || hasAccess(currentUser, 'users', 'Lectura');
const canEditUsers = canManageProtected || hasAccess(currentUser, 'users', 'Edición');
const canDeleteUsers = canManageProtected || hasAccess(currentUser, 'users', 'Eliminación');


  // === Carga acotada de usuarios (sin stream masivo) ===
  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      if (!canReadUsers) {
        setUsers([]);
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, 'users'), limit(100)));
        if (cancelled) return;
        const list = snap.docs.map(d => {
          const data = d.data() || {};
          const name = data.name || data.displayName || data.email || '';
          return {
            id: d.id,
            ...data,
            name,
            displayName: data.displayName || name,
            permissions: normalizePermissionsMatrix(data.permissions || {}),
            createdAt: data.createdAt || data.created_at || null,
          };
        });
        setUsers(list);
      } catch (err) {
        console.error('No se pudieron cargar usuarios', err);
      }
    };
    fetchUsers();
    return () => { cancelled = true; };
  }, [canReadUsers]);

  const callUserApi = async (action, payload) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No se pudo obtener la sesión. Vuelve a iniciar sesión.');
    }

    const res = await fetch('/api/users/manage', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...payload, action }),
    });
    const data = await parseApiJson(res);
    if (!res.ok) {
      const error = new Error(data?.error || 'No se pudo completar la operación');
      error.code = data?.code || '';
      error.response = data;
      throw error;
    }
    return data;
  };

  const adminCreateUser = async (payload) => {
    return callUserApi('create', payload);
  };

  const adminUpdateUser = async (uid, payload) => {
    return callUserApi('update', { targetUid: uid, ...payload });
  };

  const adminSendPasswordReset = async (_email, uid) => {
    return callUserApi('reset-password', { targetUid: uid });
  };

  // === Guardar (crear/editar) ===
  const handleSaveUser = async (userData) => {
    if (!canEditUsers) {
      alert('No tienes permiso de Edición en el módulo Usuarios.');
      return;
    }
    if (!userData || savingUserRef.current) return;
    savingUserRef.current = true;
    setSavingUser(true);

    try {
      const canonicalPermissions = normalizePermissions(userData.permissions || {}, userData.role);

      if (userData.id || userData.uid) {
      const uid = userData.uid || userData.id;
      const selfOrProtected = String(uid) === String(currentUser?.uid || currentUser?.id || '') || isProtected(userData);
      const saved = await callUserApi('update', selfOrProtected
        ? { targetUid: uid, displayName: userData.name }
        : {
            targetUid: uid,
            displayName: userData.name,
            role: userData.role,
            permissions: canonicalPermissions,
            isActive: userData.isActive ?? true,
          });
      const savedUser = saved?.user || {};
      const savedPermissions = normalizePermissionsMatrix(
        savedUser.permissions || canonicalPermissions
      );
      setUsers((prev) =>
        prev.map((user) =>
          String(user.uid || user.id) === String(uid)
            ? {
                ...user,
                ...savedUser,
                id: uid,
                uid,
                name: savedUser.displayName || userData.name,
                displayName: savedUser.displayName || userData.name,
                role: savedUser.role || userData.role,
                permissions: savedPermissions,
                isActive: savedUser.isActive ?? userData.isActive ?? true,
              }
            : user
        )
      );
      } else {
      if (!userData.email) {
        alert('El correo es obligatorio para crear el usuario.');
        return;
      }
      try {
        const created = await callUserApi('create', {
          email: userData.email,
          displayName: userData.name || userData.email,
          role: userData.role,
          permissions: canonicalPermissions,
          isActive: true,
        });
        const newUser = created?.user;
        if (newUser?.uid) {
          setUsers((prev) => [
            ...prev,
            {
              id: newUser.uid,
              uid: newUser.uid,
              email: newUser.email,
              name: newUser.displayName || newUser.email,
              displayName: newUser.displayName || newUser.email,
              role: newUser.role,
              permissions: normalizePermissionsMatrix(newUser.permissions || canonicalPermissions),
              isActive: newUser.isActive !== false,
              ...(created.passwordEmailSent ? { passwordResetSentAt: new Date().toISOString() } : {}),
            },
          ]);
        }
        if (created?.passwordEmailSent === false) {
          alert(created?.warning?.message || 'Usuario creado. El correo de contraseña queda pendiente y puede reintentarse.');
        }
      } catch (e) {
        if (String(e?.message || '').toLowerCase().includes('correo ya existe')) {
          alert('Este correo ya está registrado en Firebase Auth. Usa otro correo o edita el usuario existente.');
          return;
        }
        throw e;
      }
      }

      setShowForm(false);
      setEditingUser(null);
    } catch (error) {
      alert(error?.message || 'No se pudo guardar el usuario.');
    } finally {
      savingUserRef.current = false;
      setSavingUser(false);
    }
  };

  // === Activar/Desactivar (no borrar en Auth) ===
const handleDeactivate = async (uid, targetState = false, targetEmail) => {
  if (!canEditUsers) {
    alert('No tienes permiso de Edición en el módulo Usuarios.');
    return;
  }
  if ((isProtected(targetEmail) && targetState === false) || String(uid) === String(currentUser?.uid || currentUser?.id || '')) {
    alert('Esta cuenta no puede desactivarse desde esta operación.');
    return;
  }
    try {
      await callUserApi('update', { targetUid: uid, isActive: targetState });
      setUsers((prev) =>
        prev.map((u) =>
          String(u.uid || u.id) === String(uid)
            ? { ...u, isActive: targetState }
            : u
        )
      );
    } catch (error) {
      alert(error?.message || 'No se pudo cambiar el estado del usuario.');
    }
};

const handleResetPassword = async (user) => {
  if (!canEditUsers) {
    alert('No tienes permiso de Edición en el módulo Usuarios.');
    return;
  }
  try {
    await callUserApi('reset-password', {
      targetUid: user.uid || user.id,
    });
    setUsers((prev) =>
      prev.map((u) =>
        String(u.uid || u.id) === String(user.uid || user.id)
          ? { ...u, passwordResetSentAt: new Date().toISOString() }
          : u
      )
    );
    alert('Le enviamos un correo para restablecer la contraseña.');
  } catch (e) {
    console.error(e);
    alert(e?.message || 'No se pudo enviar el correo de restablecimiento.');
  }
};


const handleEditUser = (user) => {
  if (!canEditUsers) {
    alert('No tienes permiso de Edición en el módulo Usuarios.');
    return;
  }
  if (isProtected(user) && !canManageProtected) {
    alert('Este usuario está protegido. Solo puede gestionarse desde la propia cuenta programacionsebastian94@gmail.com.');
    return;
  }
  setEditingUser({
    ...user,
    name: user?.name || user?.displayName || user?.email || '',
    permissions: normalizePermissions(user.permissions, user.role),
  });
  setShowForm(true);
};

  const startNewUser = () => {
    if (!canEditUsers) {
      alert('No tienes permiso de Edición en el módulo Usuarios.');
      return;
    }
    setEditingUser({ name: '', email: '', role: '', permissions: {}, isActive: true });
    setShowForm(true);
  };

  const handleDeleteUser = async (user) => {
    if (!canDeleteUsers) {
      setDeleteMessage('No tienes permiso de Eliminación en el módulo Usuarios.');
      return;
    }
    const uid = user?.uid || user?.id;
    if (!uid) {
      setDeleteMessage('No se pudo identificar el usuario a eliminar.');
      return;
    }
    if (isProtected(user)) {
      setDeleteMessage('Este usuario está protegido y no se puede eliminar.');
      return;
    }
    setDeletingUid(uid);
    setDeleteMessage('');
    try {
      await callUserApi('delete', { targetUid: uid });

      setUsers((prev) => prev.filter((u) => String(u.uid || u.id) !== String(uid)));
      setDeleteMessage('Usuario eliminado en Auth y Firestore.');
      setConfirmingUser(null);
    } catch (e) {
      console.error('delete user error', e);
      setDeleteMessage(e?.message || 'No se pudo eliminar el usuario');
    } finally {
      setDeletingUid(null);
    }
  };

  if (!canReadUsers) {
    return (
      <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 px-4 py-2 rounded-lg border border-blue-200"
        >
          Volver al Menú
        </button>
        <div className="mt-10 max-w-xl mx-auto bg-white rounded-xl shadow p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800">Acceso restringido</h1>
          <p className="mt-3 text-gray-600">No tienes permiso de Lectura en el módulo Usuarios.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Gestión de Usuarios y Roles</h1>
        {canEditUsers ? (
          <button
            onClick={startNewUser}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-md hover:shadow-lg flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Usuario
          </button>
        ) : (
          <span className="px-4 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium">Solo lectura</span>
        )}
      </div>

      {showForm ? (
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            {editingUser?.id ? 'Editar Usuario' : 'Registrar Nuevo Usuario'}
          </h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveUser(editingUser);
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
                <input
                  type="text"
                  value={editingUser?.name || ''}
                  onChange={(e) => setEditingUser((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
                <input
                  type="email"
                  value={editingUser?.email || ''}
                  onChange={(e) => setEditingUser((p) => ({ ...p, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required={!editingUser?.id}
                  disabled={!!editingUser?.id}
                />
                {!editingUser?.id && (
                  <p className="text-xs text-gray-500 mt-1">
                    Se enviará un correo para que el usuario defina su contraseña.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  value={editingUser?.role || ''}
                  onChange={(e) =>
                    setEditingUser((p) => ({
                      ...p,
                      role: e.target.value,
                      permissions: normalizePermissions({}, e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                  disabled={restrictSensitiveEditing}
                >
                  <option value="">Seleccionar Rol</option>
                  {roles.filter((r) => r !== 'TI' || editingUser?.role === 'TI').map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {editingUser?.id && (
                <div className="flex items-center gap-3">
                  <input
                    id="activo"
                    type="checkbox"
                    checked={editingUser?.isActive ?? true}
                    onChange={(e) => setEditingUser((p) => ({ ...p, isActive: e.target.checked }))}
                    disabled={restrictSensitiveEditing}
                  />
                  <label htmlFor="activo" className="text-sm text-gray-700">Usuario activo</label>
                </div>
              )}
            </div>

            {editingUser?.role && (
              <div className="mt-8 border-t pt-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Permisos por Módulo</h3>
                <div className="grid grid-cols-1 gap-4">
                  {(() => {
                    const email = (currentUser?.email || '').trim().toLowerCase();
                    const canShowNotice = email === PROTECTED_EMAIL;
                    const modulesToRender = canShowNotice ? modules : modules.filter((m) => m !== 'Avisos Globales');
                    return modulesToRender;
                  })().map((moduleName) => (
                    <div key={moduleName} className="bg-gray-50 p-4 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-2">{moduleName}</h4>
                      <div className="flex flex-wrap gap-4">
                        {(moduleName === 'Panel de Despacho' ? [...permissionsTypes, 'Cambiar mesa'] : permissionsTypes).map((permType) => (
                          <label key={`${moduleName}-${permType}`} className="inline-flex items-center">
                            <input
                              type="checkbox"
                              className="h-5 w-5"
                              checked={editingUser.permissions?.[moduleName]?.[permType] === true}
                              disabled={restrictSensitiveEditing}
                              onChange={(e) => {
                                const next = { ...(editingUser.permissions || {}) };
                                next[moduleName] = { ...(next[moduleName] || {}), [permType]: e.target.checked };
                                setEditingUser((p) => ({ ...p, permissions: next }));
                              }}
                            />
                            <span className="ml-2 text-gray-700">{permType}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-end space-x-3">
              <button type="button" disabled={savingUser} onClick={() => { setShowForm(false); setEditingUser(null); }} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-60">Cancelar</button>
              <button type="submit" disabled={savingUser} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
                {savingUser ? 'Guardando...' : editingUser?.id ? 'Guardar Cambios' : 'Registrar Usuario'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último Acceso</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último Reset</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length > 0 ? (
                  users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.name || u.displayName || u.email || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.role || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatTS(u.lastLogin)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatTS(u.passwordResetSentAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {u.isActive ? <span className="text-emerald-700">Activo</span> : <span className="text-red-600">Inactivo</span>}
                      </td>
<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex gap-3">
  {(() => {
    const protectedAndLocked = isProtected(u) && !canManageProtected;

    return (
      <>
        <button
          onClick={() => handleEditUser(u)}
          disabled={!canEditUsers || protectedAndLocked}
          className={
            ((!canEditUsers || protectedAndLocked) ? 'opacity-40 cursor-not-allowed ' : '') +
            'text-blue-600 hover:text-blue-800'
          }
          title={!canEditUsers ? 'Sin permiso de Edición' : (protectedAndLocked ? 'Usuario protegido: solo puede editarse desde programacionsebastian94@gmail.com' : 'Editar')}
        >
          Editar
        </button>

        <button
          onClick={() => handleResetPassword(u)}
          disabled={!canEditUsers}
          className={(!canEditUsers ? 'opacity-40 cursor-not-allowed ' : '') + 'text-indigo-600 hover:text-indigo-800'}
          title={!canEditUsers ? 'Sin permiso de Edición' : 'Restablecer clave'}
        >
          Restablecer clave
        </button>

        <button
          onClick={() => handleDeactivate(u.uid || u.id, !u.isActive, u.email)}
          disabled={!canEditUsers || protectedAndLocked}
          className={
            ((!canEditUsers || protectedAndLocked)
              ? 'opacity-40 cursor-not-allowed '
              : (u.isActive ? 'text-red-600 hover:text-red-800' : 'text-emerald-700 hover:text-emerald-900'))
          }
          title={!canEditUsers ? 'Sin permiso de Edición' : (protectedAndLocked ? 'Usuario protegido: no se puede desactivar desde otras cuentas' : (u.isActive ? 'Desactivar' : 'Activar'))}
        >
          {u.isActive ? 'Desactivar' : 'Activar'}
        </button>

        <button
          onClick={() => setConfirmingUser(u)}
          disabled={!canDeleteUsers || protectedAndLocked || deletingUid === (u.uid || u.id)}
          className={
            ((!canDeleteUsers || protectedAndLocked) ? 'opacity-40 cursor-not-allowed ' : '') +
            'text-red-700 hover:text-red-900'
          }
          title={!canDeleteUsers ? 'Sin permiso de Eliminación' : (protectedAndLocked ? 'Usuario protegido: no se puede eliminar desde otras cuentas' : 'Eliminar definitivamente')}
        >
          {deletingUid === (u.uid || u.id) ? 'Eliminando...' : 'Eliminar'}
        </button>
      </>
    );
  })()}
</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">No hay usuarios registrados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      <DeleteUserModal
        user={confirmingUser}
        deleting={!!deletingUid}
        message={deleteMessage}
        onCancel={() => {
          if (!deletingUid) {
            setConfirmingUser(null);
            setDeleteMessage('');
          }
        }}
        onConfirm={() => {
          if (confirmingUser) handleDeleteUser(confirmingUser);
        }}
      />
    </div>
  );
};

export default UserManagementView;

// === Modal de confirmación de eliminación ===
// Se renderiza dentro del mismo archivo para evitar dependencias externas.
const DeleteUserModal = ({ user, onCancel, onConfirm, deleting, message }) => {
  if (!user) return null;
  const email = user.email || '';
  const role = user.role || '';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 space-y-5">
        <div className="space-y-1">
          <p className="text-lg font-semibold text-gray-900">Eliminar usuario</p>
          <p className="text-sm text-gray-600">
            Esta acción eliminará al usuario del sistema y de Firebase Auth. No se puede deshacer.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm text-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Correo</span>
            <span className="font-semibold text-gray-900 break-all text-right">{email || 'Sin correo'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Rol</span>
            <span className="font-semibold text-gray-900">{role || '—'}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {deleting ? 'Eliminando...' : 'Eliminar definitivamente'}
          </button>
        </div>

        {message ? (
          <div
            className={`text-sm mt-1 ${message.toLowerCase().includes('elimin') ? 'text-emerald-700' : 'text-red-700'}`}
          >
            {message}
          </div>
        ) : null}
      </div>
    </div>
  );
};
